import { useFormikContext } from 'formik';
import _ from 'lodash';
import { useContext, useEffect, useMemo } from 'react';
import { FormFeedback, FormGroup, Label } from 'reactstrap';
import {
  categoryOptions,
  getOperationUnitOptions,
  getOperationUnitSpecMap,
  groupOptions,
} from '../../../constants/operations';
import { isSpecialRevenueStream } from '../../../constants/revenueStreams';
import { useUnitsMap } from '../../../contexts/UnitContext';
import {
  asDuration,
  asMilliseconds,
  asQuantityWithUnit,
} from '../../../utils/formatters';
import OperationInputs from './OperationInputs';
import {
  DropdownButtonField,
  NameInput,
  OptionalSection,
  ValidatedInput,
  YearInputs,
} from '../template-components';
import TypeaheadWithSubtext from '../../basics/TypeaheadWithSubtext';
import CapexEquipmentTypeSection from './CapexEquipmentTypeSection';
import TimingSection from './TimingSection';
import OperationCapexGroups from './OperationCapexGroups';
import { SidebarEditContext } from '../../../contexts/SidebarEditContext';
import EquipmentSelector from './EquipmentSelector';
import { isAsterisk, multiplePlaceholder } from '../../../utils/bulkEdits';
import { useRenderWithBulkErrors } from '../../../hooks/useRenderWithBulkErrors';
import OperationDurationFormGroup from './OperationDurationFormGroup';

const formatAvgPercent = (percentArr) =>
  _.mean(percentArr.filter((percent) => percent != null)).toFixed(2);

const CropOperationsForm = ({ showNotes, resource, editing }) => {
  const {
    values,
    setValues,
    touched,
    errors,
    dirty,
    isValid,
  } = useFormikContext();

  const { setIsDirty, setHasValidationError } = useContext(SidebarEditContext);

  const {
    renderWithBulkErrors,
    renderObjectBulkError,
  } = useRenderWithBulkErrors('operations');

  const lossEventOptions = useMemo(
    () =>
      (resource?.lossEvents || []).filter(
        (lossEvent) => lossEvent.percentTreeLoss != null,
      ),
    [resource],
  );
  const revenueStreamOptions = useMemo(
    () =>
      (resource?.revenueStreams || []).filter(
        ({ name }) => !isSpecialRevenueStream(name),
      ),
    [resource],
  );

  const operationUnitSpecMap = getOperationUnitSpecMap(resource?.yield?.unit);

  const operationUnitsMap = useUnitsMap(operationUnitSpecMap);

  const unitOptions = useMemo(
    () => getOperationUnitOptions(operationUnitsMap),
    [operationUnitsMap],
  );

  const runEveryYearProps = {
    cadence: 1,
    startingYear: 1,
    endingYear: null,
    timesPerYear: 1,
    years: null,
  };
  const showMillisecondsOverrides =
    !!values.millisecondsOverrides &&
    Object.values(values.millisecondsOverrides).some((v) => v != null);

  useEffect(() => {
    setIsDirty(dirty);
  }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setHasValidationError(!isValid);
  }, [isValid]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <FormGroup className="d-inline-block">
        {values.operations ? (
          <div className="font-weight-bold">{`${asQuantityWithUnit(
            'Operations',
            'Operation',
          )(values.operations.length)} selected for editing`}</div>
        ) : (
          <NameInput name="name" placeholder="Operation name" />
        )}
      </FormGroup>
      <div className="float-right d-flex flex-column align-items-end">
        <div className="d-flex align-items-center mb-2">
          <div>Category: </div>
          <DropdownButtonField
            inModal
            size="sm"
            className="ml-2"
            options={categoryOptions}
            placeholder={multiplePlaceholder(values.category)}
            name="category"
          />
        </div>
        <div className="d-flex align-items-center">
          <div>Group: </div>
          <DropdownButtonField
            inModal
            size="sm"
            className="ml-2"
            options={groupOptions}
            placeholder={multiplePlaceholder(values.group)}
            name="group"
          />
        </div>
      </div>
      <CapexEquipmentTypeSection
        unitOptions={unitOptions}
        operationUnitSpecMap={operationUnitSpecMap}
      />
      <div className="d-flex flex-wrap">
        <OperationDurationFormGroup
          operationUnitSpecMap={operationUnitSpecMap}
          unitOptions={unitOptions}
        />
        <ValidatedInput
          maxWidth="75px"
          name="numberOfLaborers"
          type="number"
          min={1}
          label="Laborers"
          placeholder={multiplePlaceholder(values.numberOfLaborers)}
          size="med"
        >
          {renderWithBulkErrors('numberOfLaborers')}
        </ValidatedInput>
      </div>
      <FormGroup>
        <OptionalSection
          forceShown={showMillisecondsOverrides}
          buttonContent="Add duration overrides"
          sectionKey={`duration-${editing?.id}`}
        >
          <YearInputs
            accessor="millisecondsOverrides"
            inputType="string"
            indexOffset={1} // Year 1 is index 1
            label="Duration overrides (hr:min:sec)"
            unitSpec={operationUnitSpecMap[values.unitForMilliseconds]}
            format={asDuration}
            parse={asMilliseconds}
            size="med"
          >
            {renderWithBulkErrors(
              'millisecondsOverrides',
              renderObjectBulkError,
            )}
          </YearInputs>
        </OptionalSection>
      </FormGroup>
      {values.operations == null && (
        <>
          <FormGroup className="mr-0">
            <Label>Equipment</Label>
            {values?.contracted || values?.rented ? (
              <EquipmentSelector name="equipmentName" />
            ) : (
              <OperationCapexGroups />
            )}
          </FormGroup>
          <OperationInputs
            unitOptions={unitOptions}
            unitsMap={operationUnitsMap}
          />
        </>
      )}
      {(values.unitForMilliseconds === 'tree' ||
        values.unitForMilliseconds === 'lb') && (
        <div className="d-flex">
          {values.unitForMilliseconds === 'tree' ? (
            <FormGroup>
              <Label>Allocate to Loss Event</Label>
              <TypeaheadWithSubtext
                maxWidth="300px"
                options={lossEventOptions}
                getSubtext={(option) => `${option.percentTreeLoss}%`}
                selectedOption={
                  isAsterisk(values.lossEvents) ? [] : values.lossEvents
                }
                placeholder={multiplePlaceholder(values.lossEvents)}
                onChange={(v) => {
                  setValues({
                    ...values,
                    lossEvents: v,
                    ...runEveryYearProps,
                  });
                }}
              />
              {touched?.lossEvents && errors?.lossEvents && (
                <FormFeedback className="d-block">
                  {errors?.lossEvents}
                </FormFeedback>
              )}
              {renderObjectBulkError('lossEvents')}
            </FormGroup>
          ) : null}
          {values.unitForMilliseconds === 'lb' ? (
            <FormGroup>
              <Label>Allocate to Sales Channel</Label>
              <TypeaheadWithSubtext
                maxWidth="300px"
                options={revenueStreamOptions}
                getSubtext={(option) => `${formatAvgPercent(option.percent)}%`}
                selectedOption={
                  isAsterisk(values.revenueStreams) ? [] : values.revenueStreams
                }
                placeholder={multiplePlaceholder(values.revenueStreams)}
                onChange={(v) => {
                  setValues({
                    ...values,
                    revenueStreams: v,
                    ...runEveryYearProps,
                  });
                }}
              />
              {touched?.revenueStreams && errors?.revenueStreams && (
                <FormFeedback className="d-block">
                  {errors?.revenueStreams}
                </FormFeedback>
              )}
              {renderObjectBulkError('revenueStreams')}
            </FormGroup>
          ) : null}
        </div>
      )}
      <TimingSection />
      {values.operations == null && showNotes && (
        <OptionalSection
          forceShown={!!values.notes || !!values.calculationExplanation}
          buttonContent="Add notes"
        >
          <ValidatedInput
            className="w-100"
            type="text"
            name="calculationExplanation"
            label="Calculation explanation"
            formGroupClassName="mr-0"
            size="med"
          />
          <ValidatedInput
            className="w-100"
            type="textarea"
            name="notes"
            label="Notes"
            formGroupClassName="mr-0"
            size="med"
          />
        </OptionalSection>
      )}
    </>
  );
};

export default CropOperationsForm;
